export const formatMoney = (minorUnits: number, currency: string): string => {
  const symbol = currency === 'INR' ? '₹' : currency === 'CAD' ? 'C$' : '$';
  const major = minorUnits / 100;
  const whole = Number.isInteger(major);
  return `${symbol}${major.toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * The price a page prints. When prices exclude GST (9 October 2026) the rupee price is followed
 * by the tax it will carry at checkout, so "₹399" never reads as the amount charged.
 */
export const priceLabel = (p: { launchAmount: number; currency: string; taxLabel: string; taxInclusive: boolean; taxAmount?: number }): string => {
  const price = formatMoney(p.launchAmount, p.currency);
  return p.taxInclusive || p.taxAmount === 0 ? price : `${price} + ${p.taxLabel}`;
};

/**
 * Dates come from the server as instants that mean a day — a renewal, an expiry,
 * the day a report was prepared. Rendering them in the viewer's timezone moves
 * that day: a midnight-UTC expiry shows a day early anywhere west of UTC, which
 * is every parent in the US and Canada. Formatting in UTC keeps the app, the
 * API and the PDF all naming the same day.
 */
export const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

export const ageLabel = (years: number, months: number): string =>
  `${years} ${years === 1 ? 'year' : 'years'}${months ? ` ${months} ${months === 1 ? 'month' : 'months'}` : ''}`;

export const initials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');

export const REPORT_STATUS: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-sage text-teal' },
  generating: { label: 'Preparing', className: 'bg-apricot/40 text-ink' },
  preview_available: { label: 'Preview ready', className: 'bg-sage text-teal' },
  payment_pending: { label: 'Payment pending', className: 'bg-apricot/60 text-ink' },
  unlocked: { label: 'Unlocked', className: 'bg-teal text-white' },
  failed: { label: 'Needs a retry', className: 'bg-apricot text-ink' },
};

export const ORDER_STATUS: Record<string, { label: string; className: string }> = {
  created: { label: 'Started', className: 'bg-sage text-teal' },
  pending: { label: 'Pending', className: 'bg-apricot/60 text-ink' },
  paid: { label: 'Paid', className: 'bg-teal text-white' },
  failed: { label: 'Failed', className: 'bg-apricot text-ink' },
  cancelled: { label: 'Cancelled', className: 'bg-sage text-ink-muted' },
  refunded: { label: 'Refunded', className: 'bg-sage text-teal' },
};
